"use client"

import * as React from "react"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { Calendar } from "./calendar"

export interface DatePickerProps {
  mode?: "single" | "range"
  selected?: Date | { from: Date; to?: Date }
  onSelect?: (date: Date | { from: Date; to?: Date } | undefined) => void
  disabled?: (date: Date) => boolean
  placeholder?: string
  className?: string
}

export function DatePicker({
  mode = "single",
  selected,
  onSelect,
  disabled,
  placeholder = "Pick a date",
  className = "",
}: DatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [internalSelected, setInternalSelected] = React.useState(selected)
  const [isMounted, setIsMounted] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Handle hydration
  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  // Close calendar when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Update internal state when external selected changes
  React.useEffect(() => {
    setInternalSelected(selected)
  }, [selected])

  const handleSelect = (date: Date | { from: Date; to?: Date } | undefined) => {
    setInternalSelected(date)
    onSelect?.(date)

    // Close calendar for single mode or when range is complete (both from and to are set)
    if (mode === "single") {
      setIsOpen(false)
    } else if (mode === "range" && date && typeof date === "object" && 'from' in date && date.from && date.to) {
      // Only close when we have both start and end dates for range selection
      setIsOpen(false)
    }
    // For range mode, keep calendar open if we only have 'from' date (incomplete range)
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getDisplayText = () => {
    // Prevent hydration mismatch by showing placeholder until mounted
    if (!isMounted) return placeholder

    if (!internalSelected) return placeholder

    if (mode === "single" && internalSelected instanceof Date) {
      return formatDate(internalSelected)
    }

    if (mode === "range" && typeof internalSelected === "object" && 'from' in internalSelected) {
      const { from, to } = internalSelected
      if (from && to) {
        return `${formatDate(from)} - ${formatDate(to)}`
      } else if (from) {
        return `${formatDate(from)} - ...`
      }
    }

    return placeholder
  }

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    setInternalSelected(undefined)
    onSelect?.(undefined)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
      >
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className={`${internalSelected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {getDisplayText()}
          </span>
        </div>

        {internalSelected && (
          <button
            onClick={clearSelection}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <X className="h-3 w-3 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 z-50">
          <Calendar
            mode={mode}
            selected={internalSelected}
            onSelect={handleSelect}
            disabled={disabled}
            className="shadow-lg"
          />
        </div>
      )}
    </div>
  )
}

// Preset date ranges for quick selection
export const dateRangePresets = [
  {
    label: "Today",
    getValue: () => ({
      from: new Date(),
      to: new Date()
    })
  },
  {
    label: "Yesterday",
    getValue: () => {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return {
        from: yesterday,
        to: yesterday
      }
    }
  },
  {
    label: "Last 7 days",
    getValue: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 6)
      return {
        from: start,
        to: end
      }
    }
  },
  {
    label: "Last 30 days",
    getValue: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 29)
      return {
        from: start,
        to: end
      }
    }
  },
  {
    label: "Last 90 days",
    getValue: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 89)
      return {
        from: start,
        to: end
      }
    }
  },
  {
    label: "This month",
    getValue: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth(), 1)
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
      return {
        from: start,
        to: end
      }
    }
  },
  {
    label: "Last month",
    getValue: () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end = new Date(now.getFullYear(), now.getMonth(), 0)
      return {
        from: start,
        to: end
      }
    }
  }
]

// Enhanced date range picker with presets
export interface DateRangePickerProps extends Omit<DatePickerProps, 'mode'> {
  showPresets?: boolean
  presets?: typeof dateRangePresets
}

export function DateRangePicker({
  selected,
  onSelect,
  disabled,
  placeholder = "Pick a date range",
  className = "",
  showPresets = false, // Disable presets for now to focus on calendar functionality
  presets = dateRangePresets,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [internalSelected, setInternalSelected] = React.useState(selected)
  const [isMounted, setIsMounted] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Handle hydration
  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  // Close when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Update internal state when external selected changes
  React.useEffect(() => {
    setInternalSelected(selected)
  }, [selected])

  const handleSelect = (date: Date | { from: Date; to?: Date } | undefined) => {
    setInternalSelected(date)
    onSelect?.(date)

    // Only close calendar when range is complete (both from and to are set)
    if (date && typeof date === "object" && 'from' in date && date.from && date.to) {
      setIsOpen(false)
    }
    // Keep calendar open if we only have 'from' date (incomplete range)
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getDisplayText = () => {
    // Prevent hydration mismatch by showing placeholder until mounted
    if (!isMounted) return placeholder

    if (!internalSelected) return placeholder

    if (typeof internalSelected === "object" && internalSelected && 'from' in internalSelected) {
      const { from, to } = internalSelected
      if (from && to) {
        return `${formatDate(from)} - ${formatDate(to)}`
      } else if (from) {
        return `${formatDate(from)} - ...`
      }
    }

    return placeholder
  }

  const clearSelection = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setInternalSelected(undefined)
    onSelect?.(undefined)
  }

  const handlePresetSelect = (preset: typeof dateRangePresets[0]) => {
    const range = preset.getValue()
    setInternalSelected(range)
    onSelect?.(range)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors"
      >
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className={`${internalSelected ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {getDisplayText()}
          </span>
        </div>

        {internalSelected && (
          <button
            onClick={clearSelection}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <X className="h-3 w-3 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-2 z-50"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <Calendar
            mode="range"
            selected={internalSelected}
            onSelect={handleSelect}
            disabled={disabled}
            className="shadow-lg"
          />
        </div>
      )}

      {/* Presets Dropdown */}
      {isOpen && showPresets && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">Quick Select</div>
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePresetSelect(preset)}
                className="w-full text-left px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
